/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from "react";
import { FieldArray, Field, Form, Formik } from "formik";
import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import * as yup from "yup";
import Icon from "components/Icon";
import { GridColumnTitle, Label } from "components/Form";
import Row from "components/Grid/Row";
import Col from "components/Grid/Col";
import ComboBox from "components/Form/ComboBox3";
import Button from "components/Button";
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableColumn,
  ViewText,
  ButtonContainer,
  ButtonContent,
  InlineLabel,
  LabelEdit
} from "../products/styles";
import { FitinsurAPI, MessageCenter } from "utils";

const Currencies = ({ hasPermission, productCurrencies, currencies, idProduct, onSaveCurrency }) => {
  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});
  const [sortBy, setSortBy] = useState({ key: null, direction: null });
  const [addCurrency, setAddCurrency] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productCurrenciesList, setProductCurrencies] = useState(productCurrencies);
  const [initial, setInitial] = useState({
    currency: ''
  });

  const columns = [
    {
      label: "CURRENCY CODE",
    },
    {
      label: "Action",
      right: true
    },
  ];

  const resetForm = () => {
    formikRef.current.values.currency = '';
  };
  
  const createValidation = () => {
    let validations = yup.object().shape({
      currency: yup.string().required("* Required field")
    });
    setValidations(validations);
    return validations;
  };

  useEffect(() => {
    createValidation();
  }, []);
  useEffect(() => {
    setProductCurrencies(productCurrencies);
  }, [productCurrencies]);

  const saveCurrency = async (body) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.updateProductCurrencies(body, idProduct)
        .then(res => res.json());
      
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setAddCurrency(false);
      resetForm();
      onSaveCurrency && onSaveCurrency();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const getFiledOptions = () => {
    const filteredCurrencies = filterAlreadyAddedCurrencies();
    return filteredCurrencies.map(option => {
      return {
        label: option.currencyCode,
        value: {
          currencyId: option.currencyId,
          currencyCode: option.currencyCode,
        }
      }
    });
  }

  const filterAlreadyAddedCurrencies = () => {
    let filteredCurrencies = [];
    if(productCurrenciesList && productCurrenciesList.length > 0){
      for (const currency of productCurrenciesList){
        if(!filteredCurrencies.length)
          filteredCurrencies = currencies.filter(item => item.currencyCode !== currency.currencyCode);
        else{
          filteredCurrencies = filteredCurrencies.filter(item => item.currencyCode !== currency.currencyCode);
        }
      }
    } else{
      filteredCurrencies = currencies
    }
    return filteredCurrencies;
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={values => saveCurrency(values.currency)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete 
            show={deleteModalOpen} 
            onModalClose={() => { setDeleteModalOpen(false); setCurrency(null); }} 
            onConfirm={() => {saveCurrency(currency);
               if(productCurrenciesList.length == 1){
                  setProductCurrencies([])
                }
            }}
          />
          <Table>
            <TableHead>
              <tr>
                {columns.map((column, index) => (
                  <TableHeader
                    right={column.right}
                    key={index}
                    minWidth={column.minWidth}
                    maxWidth={column.maxWidth}
                  >
                    <GridColumnTitle
                      style={{
                        cursor: column.value ? "pointer" : "not-allowed",
                      }}
                    >
                      {column.label}
                    </GridColumnTitle>
                    {column.value &&
                    sortBy.key === column.value &&
                    sortBy.direction === "asc" ? (
                      <Icon name={"arrowup"} />
                    ) : column.value &&
                      sortBy.key === column.value &&
                      sortBy.direction === "desc" ? (
                      <Icon name={"arrowdown"} />
                    ) : (
                      ""
                    )}
                  </TableHeader>
                ))}
              </tr>
            </TableHead>
            <TableBody>
              <FieldArray
                name="import preview"
                render={() => (
                  productCurrenciesList && productCurrenciesList.map(currency => (
                    <tr key={currency.currencyId}>
                      <TableColumn>
                        <InlineLabel>
                          CURRENCY CODE
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {currency.currencyCode}
                        </LabelEdit>
                      </TableColumn>

                      <TableColumn right>
                        <InlineLabel>Action</InlineLabel>
                        <Label>
                          <Icon
                            name="trash"
                            style={{ alignSelf: "center", cursor: "pointer" }}
                            onClick={() => {
                              setDeleteModalOpen(true);
                              setCurrency(currency);
                              resetForm();
                            }}
                          />
                        </Label>
                      </TableColumn>
                    </tr>
                  ))
                )}
              />
            </TableBody>
          </Table>

          {!addCurrency && (
            <Row>
              <Col lg={6} md={6} sm={6} xs={12}></Col>
              <Col lg={6} md={6} sm={6} xs={12}>
                <ViewText style={{ marginTop: "15px" }}>
                  <Icon
                    name="plus"
                    onClick={() => { setAddCurrency(true) }}
                    style={{ cursor: "pointer" }}
                  />
                </ViewText>
              </Col>
            </Row>
          )}

          {addCurrency && (
            <Form>
              <div style={{ marginTop: 40, marginBottom: 100 }}>
                <Row>
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <Field
                      name="currency"
                      label="Currency"
                      options={getFiledOptions()}
                      searchList={getFiledOptions()}
                      onChange={(e) => {
                        setFieldValue('currency', e)
                      }}
                      disabled={!hasPermission}
                      component={ComboBox}
                      isRequired
                    />
                  </Col>
                </Row>
                <ButtonContainer>
                  <ButtonContainer
                    style={{
                      justifyContent: "flex-start",
                      alignItems: "center",
                      paddingBottom: "10px",
                    }}
                  />
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      third
                      onClick={() => {
                        setAddCurrency(false);
                        resetForm();
                      }}
                    >
                      CANCEL
                    </Button>
                  </ButtonContent>
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      type="submit"
                      primary
                      onClick={() => {
                        createValidation()
                          .validate(values)
                          .catch((e) => {
                            console.log("validation error", e);
                            mc.error("Please fill in all required fields.");
                          });
                      }}
                      id="btnSubmit"
                    >
                      ADD CURRENCY
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </div>
            </Form>
          )}
        </>
      )}
    </Formik>
  );
};

export default Currencies;
